/* Visor de UNA imagen de la ficha de producto (Dog Armour).
 *
 * Al pulsar una foto de la galeria abre ESA foto, no la galeria entera:
 * se carga a resolucion alta y se puede ampliar pulsando, con la rueda o
 * pellizcando, y arrastrar para moverse.
 *
 * Notas que no son de estilo:
 *  - solo se anima `transform` (nunca width/height/top/left): asi va a 60fps.
 *  - durante el arrastre se quita la transicion, o la imagen persigue al dedo
 *    con un cuarto de segundo de retraso.
 *  - la URL de la imagen se pide al CDN con el `width` que necesita ESTA
 *    pantalla; servir siempre 3840 px son megas tirados en un movil.
 */
(() => {
  const MIN = 1;

  class DarZoom {
    constructor(dialogo) {
      this.dialogo = dialogo;
      this.stage = dialogo.querySelector('[data-dar-zoom-stage]');
      // la imagen se crea aqui (ver el comentario de la seccion): cada foto trae
      // su propia proporcion, asi que no puede venir declarada en el Liquid
      this.img = document.createElement('img');
      this.img.className = 'dar-zoom__img';
      this.img.draggable = false;
      this.img.alt = '';
      this.stage.appendChild(this.img);
      this.max = parseFloat(dialogo.dataset.maxZoom) || 2.5;

      this.escala = MIN;
      this.x = 0;
      this.y = 0;
      this.origen = null; // el contenedor pulsado, para devolverle el foco
      this.punteros = new Map();
      this.arrastrando = false;
      this.movido = false;
      this.pinchaInicial = 0;

      dialogo.querySelector('[data-dar-zoom-close]')?.addEventListener('click', () => this.cerrar());
      dialogo.addEventListener('close', () => this.alCerrar());
      // click en el fondo (fuera de la imagen) = cerrar
      this.stage.addEventListener('click', (e) => {
        if (this.movido) return;
        if (e.target === this.img) this.alternar(e);
        else this.cerrar();
      });

      this.stage.addEventListener('pointerdown', (e) => this.abajo(e));
      this.stage.addEventListener('pointermove', (e) => this.mueve(e));
      this.stage.addEventListener('pointerup', (e) => this.arriba(e));
      this.stage.addEventListener('pointercancel', (e) => this.arriba(e));
      this.stage.addEventListener('wheel', (e) => this.rueda(e), { passive: false });

      this.enlazarGaleria();
    }

    /* ---------- galeria ---------- */

    enlazarGaleria() {
      const galerias = document.querySelectorAll('media-gallery, .product-media-container');
      if (!galerias.length) return;

      document.querySelectorAll('.product-media-container--image').forEach((c) => {
        c.classList.add('dar-zoomable');
        // el contenedor no es un boton: se le da rol y teclado para que tambien
        // se pueda abrir con Enter/Espacio
        if (!c.hasAttribute('tabindex')) c.setAttribute('tabindex', '0');
        c.setAttribute('role', 'button');
      });

      document.addEventListener('click', (e) => {
        const cont = e.target.closest('.product-media-container--image');
        if (!cont) return;
        // los enlaces y botones de dentro (variantes, play de video) mandan
        if (e.target.closest('a, button')) return;
        const img = cont.querySelector('img.product-media__image, img');
        if (!img) return;
        e.preventDefault();
        this.abrir(img, cont);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const cont = document.activeElement?.closest?.('.product-media-container--image');
        if (!cont) return;
        const img = cont.querySelector('img.product-media__image, img');
        if (!img) return;
        e.preventDefault();
        this.abrir(img, cont);
      });
    }

    /** Pide al CDN la version que cabe en ESTA pantalla, no la de 3840 siempre. */
    urlGrande(img) {
      const base = img.currentSrc || img.src;
      if (!base) return '';
      try {
        const url = new URL(base, window.location.origin);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        // el lado que ocupa la foto ajustada a pantalla, no el mayor de los dos:
        // pedir 3840 px en un movil es descargar megas para nada
        const lado = Math.min(window.innerWidth, window.innerHeight);
        const tope = window.innerWidth < 750 ? 1800 : 3840;
        const ancho = Math.min(tope, Math.ceil(lado * dpr * 2.2));
        url.searchParams.set('width', String(ancho));
        return url.toString();
      } catch (e) {
        return base;
      }
    }

    /* ---------- abrir / cerrar ---------- */

    abrir(img, contenedor) {
      this.origen = contenedor;
      this.reset();
      this.img.src = this.urlGrande(img);
      this.img.alt = img.alt || '';
      if (!this.dialogo.open) this.dialogo.showModal();
      this.dialogo.dataset.zoomed = 'false';
      // la pista se lee una vez; a los 2,5 s estorba encima de la foto
      this.dialogo.dataset.hint = 'on';
      clearTimeout(this.tPista);
      this.tPista = setTimeout(() => { this.dialogo.dataset.hint = 'off'; }, 2500);
      document.body.classList.add('dar-zoom-abierto');
    }

    cerrar() {
      if (this.dialogo.open) this.dialogo.close();
    }

    alCerrar() {
      document.body.classList.remove('dar-zoom-abierto');
      this.reset();
      // sin esto el foco vuelve al principio de la pagina
      this.origen?.focus?.();
      this.origen = null;
    }

    reset() {
      this.escala = MIN;
      this.x = 0;
      this.y = 0;
      this.punteros.clear();
      this.arrastrando = false;
      this.movido = false;
      this.img.classList.remove('is-dragging', 'is-anim');
      this.aplicar();
    }

    /* ---------- zoom ---------- */

    aplicar() {
      this.limitar();
      this.img.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.escala})`;
      this.dialogo.dataset.zoomed = this.escala > MIN + 0.01 ? 'true' : 'false';
    }

    /** No se puede arrastrar la foto fuera de la pantalla. */
    limitar() {
      if (this.escala <= MIN) {
        this.x = 0;
        this.y = 0;
        return;
      }
      // La foto no ocupa todo el elemento (object-fit deja franjas): los limites
      // se calculan sobre la FOTO, no sobre la caja, o se puede arrastrar el
      // vacio hasta sacar la imagen de la pantalla.
      const caja = this.img.getBoundingClientRect();
      const cajaW = caja.width / this.escala;
      const cajaH = caja.height / this.escala;
      const nw = this.img.naturalWidth || cajaW;
      const nh = this.img.naturalHeight || cajaH;
      const rel = Math.min(cajaW / nw, cajaH / nh);
      const maxX = Math.max(0, (nw * rel * this.escala - window.innerWidth) / 2);
      const maxY = Math.max(0, (nh * rel * this.escala - window.innerHeight) / 2);
      this.x = Math.min(maxX, Math.max(-maxX, this.x));
      this.y = Math.min(maxY, Math.max(-maxY, this.y));
    }

    /** Amplia manteniendo bajo el dedo/raton el punto que se ha tocado. */
    zoomEn(nuevaEscala, puntoX, puntoY, animar) {
      const escala = Math.min(this.max, Math.max(MIN, nuevaEscala));
      const centroX = window.innerWidth / 2;
      const centroY = window.innerHeight / 2;
      const dx = puntoX - centroX;
      const dy = puntoY - centroY;
      const factor = escala / this.escala;

      this.x = (this.x - dx) * factor + dx;
      this.y = (this.y - dy) * factor + dy;
      this.escala = escala;

      this.img.classList.toggle('is-anim', !!animar);
      this.aplicar();
      if (animar) setTimeout(() => this.img.classList.remove('is-anim'), 300);
    }

    alternar(e) {
      const destino = this.escala > MIN + 0.01 ? MIN : this.max;
      this.zoomEn(destino, e.clientX, e.clientY, true);
    }

    rueda(e) {
      e.preventDefault();
      const paso = e.deltaY < 0 ? 1.18 : 1 / 1.18;
      this.zoomEn(this.escala * paso, e.clientX, e.clientY, false);
    }

    /* ---------- puntero: arrastrar y pellizcar ---------- */

    abajo(e) {
      this.punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.movido = false;
      if (this.punteros.size === 2) {
        this.pinchaInicial = this.distancia();
        this.escalaInicial = this.escala;
        return;
      }
      if (this.escala > MIN) {
        this.arrastrando = true;
        this.ultimo = { x: e.clientX, y: e.clientY };
        this.img.classList.add('is-dragging');
        this.stage.setPointerCapture?.(e.pointerId);
      }
    }

    mueve(e) {
      if (!this.punteros.has(e.pointerId)) return;
      this.punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.punteros.size === 2 && this.pinchaInicial) {
        const d = this.distancia();
        const centro = this.centro();
        this.movido = true;
        this.zoomEn((this.escalaInicial * d) / this.pinchaInicial, centro.x, centro.y, false);
        return;
      }

      if (!this.arrastrando) return;
      const dx = e.clientX - this.ultimo.x;
      const dy = e.clientY - this.ultimo.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.movido = true;
      this.ultimo = { x: e.clientX, y: e.clientY };
      this.x += dx;
      this.y += dy;
      this.aplicar();
    }

    arriba(e) {
      this.punteros.delete(e.pointerId);
      if (this.punteros.size < 2) this.pinchaInicial = 0;
      if (this.punteros.size === 0) {
        this.arrastrando = false;
        this.img.classList.remove('is-dragging');
        // el click posterior al arrastre no debe alternar el zoom
        setTimeout(() => { this.movido = false; }, 0);
      }
    }

    distancia() {
      const [a, b] = [...this.punteros.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    centro() {
      const [a, b] = [...this.punteros.values()];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
  }

  const arrancar = () => {
    const d = document.querySelector('[data-dar-zoom]');
    if (d && !d.darZoom && typeof HTMLDialogElement !== 'undefined') d.darZoom = new DarZoom(d);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
  document.addEventListener('shopify:section:load', arrancar);
})();
